// File Metadata:
// Filename: checklistApi.js
// Version: 1.02
// Created: 2025-06-16
// Last Modified: 2026-05-31
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for checklist templates, parent assignment management, and child self-service routines

import { isSameDay } from 'date-fns';
import { baseApi } from '../../../../store/api/baseApi';

export const checklistApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Checklist Items Endpoints
    getChecklistItems: builder.query({
      query: ({ category = null, searchTerm = null } = {}) => ({
        url: '/checklists/items',
        params: { 
          ...(category && { category }),
          ...(searchTerm && { searchTerm })
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'ChecklistItem', id: _id })),
              { type: 'ChecklistItem', id: 'LIST' }
            ]
          : [{ type: 'ChecklistItem', id: 'LIST' }],
      keepUnusedDataFor: 300, // 5 minutes cache (replaces custom caching)
    }),

    getChecklistItem: builder.query({
      query: (itemId) => `/checklists/items/${itemId}`,
      providesTags: (result, error, itemId) => [{ type: 'ChecklistItem', id: itemId }],
    }),

    createChecklistItem: builder.mutation({
      query: (itemData) => ({
        url: '/checklists/items',
        method: 'POST',
        body: itemData,
      }),
      invalidatesTags: [{ type: 'ChecklistItem', id: 'LIST' }],
      // Optimistic update
      async onQueryStarted(itemData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          checklistApi.util.updateQueryData('getChecklistItems', {}, (draft) => {
            // Add optimistic item with temporary ID
            const optimisticItem = {
              _id: `temp-${Date.now()}`,
              ...itemData,
              createdAt: new Date().toISOString(),
            };
            draft.push(optimisticItem);
          })
        );
        try {
          const { data: newItem } = await queryFulfilled;
          // Replace optimistic item with real data
          dispatch(
            checklistApi.util.updateQueryData('getChecklistItems', {}, (draft) => {
              const index = draft.findIndex(item => item._id.toString().startsWith('temp-'));
              if (index !== -1) {
                draft[index] = newItem;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateChecklistItem: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/checklists/items/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'ChecklistItem', id }],
      // Optimistic update
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          checklistApi.util.updateQueryData('getChecklistItems', {}, (draft) => {
            const item = draft.find(item => item._id === id);
            if (item) {
              Object.assign(item, patch);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteChecklistItem: builder.mutation({
      query: (id) => ({
        url: `/checklists/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ChecklistItem', id: 'LIST' }],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          checklistApi.util.updateQueryData('getChecklistItems', {}, (draft) => {
            const index = draft.findIndex(item => item._id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getChecklistItemHistory: builder.query({
      query: (itemId) => `/checklists/items/${itemId}/history`,
      providesTags: (result, error, itemId) => [{ type: 'ChecklistItem', id: `${itemId}-history` }],
    }),

    // Checklist Template Endpoints
    getChecklists: builder.query({
      query: (params = {}) => ({
        url: '/checklists',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Checklist', id: _id })),
              { type: 'Checklist', id: 'LIST' }
            ]
          : [{ type: 'Checklist', id: 'LIST' }],
    }),

    getChecklist: builder.query({
      query: (checklistId) => `/checklists/${checklistId}`,
      providesTags: (result, error, checklistId) => [{ type: 'Checklist', id: checklistId }],
    }),

    createChecklist: builder.mutation({
      query: (checklistData) => ({
        url: '/checklists',
        method: 'POST',
        body: checklistData,
      }),
      invalidatesTags: [{ type: 'Checklist', id: 'LIST' }],
    }),

    updateChecklist: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/checklists/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Checklist', id },
        { type: 'Checklist', id: 'LIST' }
      ],
    }),

    deleteChecklist: builder.mutation({
      query: (id) => ({
        url: `/checklists/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Checklist', id: 'LIST' }],
    }),

    // Checklist Assignment Endpoints
    getChecklistAssignments: builder.query({
      query: ({ userId = null, includeResets = true, date = null } = {}) => ({
        url: '/checklists/assignments',
        params: { 
          ...(userId && { userId }),
          includeResets,
          ...(date && { date })
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'ChecklistAssignment', id: _id })),
              { type: 'ChecklistAssignment', id: 'LIST' }
            ]
          : [{ type: 'ChecklistAssignment', id: 'LIST' }],
      keepUnusedDataFor: 120, // 2 minutes cache for assignments
    }),

    getMyChecklistAssignments: builder.query({
      query: ({ active = true, date = null } = {}) => ({
        url: '/checklists/me',
        params: {
          ...(active !== undefined && { active }),
          ...(date && { date })
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'ChecklistAssignment', id: _id })),
              { type: 'ChecklistAssignment', id: 'LIST' }
            ]
          : [{ type: 'ChecklistAssignment', id: 'LIST' }],
      keepUnusedDataFor: 60,
    }),

    getChecklistAssignment: builder.query({
      query: (assignmentId) => `/checklists/assignments/${assignmentId}`,
      providesTags: (result, error, assignmentId) => [{ type: 'ChecklistAssignment', id: assignmentId }],
    }),

    getMyChecklistAssignment: builder.query({
      query: (assignmentId) => `/checklists/me/${assignmentId}`,
      providesTags: (result, error, assignmentId) => [{ type: 'ChecklistAssignment', id: assignmentId }],
    }),

    createChecklistAssignment: builder.mutation({
      query: (assignmentData) => ({
        url: '/checklists/assignments',
        method: 'POST',
        body: assignmentData,
      }),
      invalidatesTags: [
        { type: 'ChecklistAssignment', id: 'LIST' },
        { type: 'ChecklistItem', id: 'LIST' }
      ],
    }),

    updateChecklistAssignment: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/checklists/assignments/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChecklistAssignment', id },
        { type: 'ChecklistAssignment', id: 'LIST' }
      ],
    }),

    deleteChecklistAssignment: builder.mutation({
      query: (id) => ({
        url: `/checklists/assignments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ChecklistAssignment', id: 'LIST' }],
    }),

    updateChecklistAssignmentItem: builder.mutation({
      query: ({ assignmentId, itemId, updateData }) => ({
        url: `/checklists/assignments/${assignmentId}/items/${itemId}`,
        method: 'PATCH',
        body: updateData,
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: 'ChecklistAssignment', id: assignmentId },
        { type: 'ChecklistAssignment', id: 'LIST' }
      ],
      // Optimistic update for item completion
      async onQueryStarted({ assignmentId, itemId, updateData }, { dispatch, queryFulfilled }) {
        const today = new Date();

        const applyOptimisticUpdate = (draft) => {
          const assignment = draft.find(a => a._id === assignmentId);
          if (assignment?.dailyResets) {
            const todayResets = assignment.dailyResets
              .filter((reset) => isSameDay(new Date(reset.date), today));
            const todayReset = todayResets.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            if (todayReset?.completedItems) {
              const item = todayReset.completedItems.find(i => i.itemId?.toString() === itemId?.toString());
              if (item) {
                Object.assign(item, updateData);
                const totalItems = todayReset.completedItems.length;
                const completedItems = todayReset.completedItems.filter(i => i.completed).length;
                todayReset.completionRate = Math.round((completedItems / totalItems) * 100);
                todayReset.isFullyCompleted = completedItems === totalItems;
              }
            }
          }
        };

        const patchResult1 = dispatch(
          checklistApi.util.updateQueryData('getChecklistAssignments', {}, applyOptimisticUpdate)
        );

        const patchResult2 = dispatch(
          checklistApi.util.updateQueryData('getMyChecklistAssignments', { active: true }, applyOptimisticUpdate)
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult1.undo();
          patchResult2.undo();
        }
      },
    }),    // Stats and Analytics

    updateMyChecklistAssignmentItem: builder.mutation({
      query: ({ assignmentId, itemId, updateData }) => ({
        url: `/checklists/me/${assignmentId}/items/${itemId}`,
        method: 'PATCH',
        body: updateData,
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: 'ChecklistAssignment', id: assignmentId },
        { type: 'ChecklistAssignment', id: 'LIST' }
      ],
      async onQueryStarted({ assignmentId, itemId, updateData }, { dispatch, queryFulfilled }) {
        const today = new Date();

        const applyOptimisticUpdate = (draft) => {
          const assignment = draft.find(a => a._id === assignmentId);
          if (assignment?.dailyResets) {
            const todayResets = assignment.dailyResets
              .filter((reset) => isSameDay(new Date(reset.date), today));
            const todayReset = todayResets.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            if (todayReset?.completedItems) {
              const item = todayReset.completedItems.find(i => i.itemId?.toString() === itemId?.toString());
              if (item) {
                Object.assign(item, updateData);
                const totalItems = todayReset.completedItems.length;
                const completedItems = todayReset.completedItems.filter(i => i.completed).length;
                todayReset.completionRate = Math.round((completedItems / totalItems) * 100);
                todayReset.isFullyCompleted = completedItems === totalItems;
              }
            }
          }
        };

        const patchResult = dispatch(
          checklistApi.util.updateQueryData('getMyChecklistAssignments', { active: true }, applyOptimisticUpdate)
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Stats and Analytics
    getChecklistAssignmentStats: builder.query({
      query: (params = {}) => ({
        url: '/checklists/assignments/stats',
        params,
      }),
      providesTags: ['ChecklistAssignment'],
      keepUnusedDataFor: 60, // 1 minute cache for stats
    }),

    // Icon management
    getAvailableIcons: builder.query({
      query: () => '/checklists/icons',
      providesTags: ['ChecklistItem'],
      keepUnusedDataFor: 300, // 5 minutes cache for icons
    }),
  }),
});

// Export hooks for use in components
export const {
  // Checklist Items
  useGetChecklistItemsQuery,
  useLazyGetChecklistItemsQuery,
  useGetChecklistItemQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetChecklistItemHistoryQuery,
  
  // Checklists
  useGetChecklistsQuery,
  useGetChecklistQuery,
  useCreateChecklistMutation,
  useUpdateChecklistMutation,
  useDeleteChecklistMutation,
  
  // Assignments
  useGetChecklistAssignmentsQuery,
  useLazyGetChecklistAssignmentsQuery,
  useGetMyChecklistAssignmentsQuery,
  useGetChecklistAssignmentQuery,
  useGetMyChecklistAssignmentQuery,
  useCreateChecklistAssignmentMutation,
  useUpdateChecklistAssignmentMutation,
  useDeleteChecklistAssignmentMutation,
  useUpdateChecklistAssignmentItemMutation,
  useUpdateMyChecklistAssignmentItemMutation,

  // Stats
  useGetChecklistAssignmentStatsQuery,
  
  // Icons
  useGetAvailableIconsQuery,
} = checklistApi;
